import React from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../../contexts/CartContext";
import CartItem from "../../../components/shared/CartItem/CartItem";
import { ChevronLeft } from "lucide-react";

const gradientClass =
  "bg-gradient-to-b from-[#484139] via-[#544C44] via-[#5D554C] via-[#655E54] to-[#6B5B4F]";

const ShoppingCart = () => {
  const {
    cartItems,
    updateQuantity,
    removeFromCart,
    clearCart,
    getTotalPrice,
    getTotalItems,
  } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    if (cartItems.length > 0) navigate("/confirm-order");
  };
  const handleContinueShopping = () => navigate("/products");

  if (cartItems.length === 0) {
    return (
      <div className={`${gradientClass} min-h-screen flex items-center justify-center px-4 py-10`}>
        <div className="text-center py-10 sm:py-12 bg-[#E3D5C5] rounded-lg border border-[#83715E] shadow-md mx-auto w-full max-w-xl px-5 sm:px-8">
          <div className="text-5xl sm:text-6xl mb-4">🛒</div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Your cart is empty</h1>
          <p className="text-gray-600 mb-6 sm:mb-8 text-sm sm:text-base">
            Looks like you haven't added any items to your cart yet.
          </p>
          <button
            onClick={handleContinueShopping}
            className="bg-[#6B5B4F] text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-[#998271] transition"
          >
            Start Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${gradientClass} min-h-screen`}>
      <div className="container mx-auto max-w-7xl py-6 sm:py-10 px-4 sm:px-6">
        {/* Back nav */}
        <button
          onClick={() => navigate("/products")}
          className="inline-flex items-center gap-1 text-[#D4B998] hover:text-[#e1caa5] mb-4 transition"
        >
          <ChevronLeft className="w-5 h-5" />
          <span className="text-sm">Back to Products</span>
        </button>

        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2 text-[#E3D5C5]">
            Shopping Cart
          </h1>
          <p className="text-sm sm:text-base text-[#D4B998]">
            {getTotalItems()} item{getTotalItems() !== 1 ? "s" : ""} in your cart
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="bg-[#E3D5C5] rounded-lg shadow-md">
              <div className="p-4 sm:p-6">
                <div className="flex justify-between items-center mb-4 sm:mb-6">
                  <h2 className="text-lg sm:text-xl font-semibold">Cart Items</h2>
                  <button
                    onClick={clearCart}
                    className="text-red-600 hover:text-red-700 text-xs sm:text-sm font-medium"
                  >
                    Clear Cart
                  </button>
                </div>

                <div className="space-y-3 sm:space-y-4">
                  {cartItems.map((item) => (
                    <CartItem
                      key={`${item.id}-${item.selectedSize}-${item.selectedColor}`}
                      item={item}
                      onUpdateQuantity={updateQuantity}
                      onRemove={removeFromCart}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="bg-[#E3D5C5] rounded-lg shadow-md p-4 sm:p-6 lg:sticky lg:top-4">
              <h2 className="text-lg sm:text-xl font-semibold mb-4">Order Summary</h2>

              <div className="space-y-3 mb-5 sm:mb-6 text-sm sm:text-base">
                <div className="flex justify-between">
                  <span>Subtotal ({getTotalItems()} items)</span>
                  <span>LKR {getTotalPrice().toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span>LKR 350.00</span>
                </div>
                <hr className="my-3 sm:my-4 border-[#83715E]/40" />
                <div className="flex justify-between text-base sm:text-lg font-semibold">
                  <span>Total</span>
                  <span>LKR {(getTotalPrice() + 350).toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                className="w-full bg-[#D4B998] text-[#463C30] py-3 rounded-full font-bold hover:bg-[#e1caa5] transition"
              >
                Proceed to Confirm
              </button>

              <button
                onClick={handleContinueShopping}
                className="w-full mt-3 border-2 border-[#D4B998] text-[#463C30] bg-transparent py-3 rounded-full font-semibold hover:bg-[#e1caa5] hover:text-[#3b332b] transition"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShoppingCart;
